import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import polarisLogo from '../images/Polaris Logo.svg';
import { fetchMarketingHubNav } from '../services/marketingHubService';
import {
    isMarketingHubNavActive,
    marketingHubPath,
    MarketingHubNavItem,
} from '../types/marketingHub';
import '../styles/marketing-hub.css';

export default function MarketingHubLayout() {
    const location = useLocation();
    const [navItems, setNavItems] = useState<MarketingHubNavItem[] | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const loadNav = async () => {
            setIsLoading(true);
            setLoadError(false);

            const items = await fetchMarketingHubNav();
            if (!isMounted) {
                return;
            }

            if (items === null) {
                setNavItems(null);
                setLoadError(true);
            } else {
                setNavItems(items);
            }
            setIsLoading(false);
        };

        void loadNav();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        setMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!menuOpen) {
            return undefined;
        }

        const handlePointerDown = (event: MouseEvent) => {
            const target = event.target as Node | null;
            if (!(target instanceof Element)) {
                setMenuOpen(false);
                return;
            }
            if (
                target.closest('.marketing-hub-nav')
                || target.closest('.marketing-hub-hamburger')
            ) {
                return;
            }
            setMenuOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        return () => document.removeEventListener('mousedown', handlePointerDown);
    }, [menuOpen]);

    const showNav = !isLoading && !loadError && Boolean(navItems && navItems.length > 0);
    const closeMenu = () => setMenuOpen(false);

    return (
        <main className="marketing-hub-page">
            <div className="marketing-hub-top">
                <div className="marketing-hub-brand">
                    <Link to="/" className="marketing-hub-brand-home" aria-label="Polaris Athlete home">
                        <img src={polarisLogo} alt="Polaris Athlete" className="marketing-hub-brand-logo" />
                    </Link>
                    {showNav && (
                        <button
                            type="button"
                            className="marketing-hub-hamburger"
                            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={menuOpen}
                            aria-controls="marketing-hub-nav"
                            onClick={() => setMenuOpen((open) => !open)}
                        >
                            <span className={`marketing-hub-hamburger-bar${menuOpen ? ' is-open' : ''}`} aria-hidden="true" />
                            <span className={`marketing-hub-hamburger-bar${menuOpen ? ' is-open' : ''}`} aria-hidden="true" />
                            <span className={`marketing-hub-hamburger-bar${menuOpen ? ' is-open' : ''}`} aria-hidden="true" />
                        </button>
                    )}
                </div>
                <Link to="/" className="marketing-hub-home-link">
                    Polaris Athlete Hub Homepage
                </Link>
            </div>
            <div className="marketing-hub-layout">
                <aside className="marketing-hub-sidebar">
                    {showNav && (
                        <nav
                            id="marketing-hub-nav"
                            className={`marketing-hub-nav${menuOpen ? ' is-open' : ''}`}
                            aria-label="Marketing Hub"
                        >
                            <p className="marketing-hub-nav-heading">Marketing Hub</p>
                            <ul className="marketing-hub-nav-list">
                                {navItems!.map((item) => {
                                    const href = marketingHubPath(item.slug);
                                    const active = isMarketingHubNavActive(item.slug, location.pathname);
                                    return (
                                        <li key={item.slug}>
                                            <NavLink
                                                to={href}
                                                end={item.slug === 'marketing-hub'}
                                                className={() =>
                                                    `marketing-hub-nav-link${active ? ' is-active' : ''}`
                                                }
                                                onClick={closeMenu}
                                            >
                                                {item.title}
                                            </NavLink>
                                        </li>
                                    );
                                })}
                            </ul>
                        </nav>
                    )}
                </aside>
                <div className="marketing-hub-content">
                    {isLoading && (
                        <p className="marketing-hub-status" role="status">Loading Marketing Hub...</p>
                    )}
                    {!isLoading && loadError && (
                        <div className="marketing-hub-status marketing-hub-status-error">
                            <p>Unable to load Marketing Hub right now. Please try again later.</p>
                            <Link to="/" className="marketing-hub-back-link">Back to home</Link>
                        </div>
                    )}
                    {!isLoading && !loadError && navItems && navItems.length === 0 && (
                        <p className="marketing-hub-status">
                            Marketing Hub is not available yet.
                        </p>
                    )}
                    {showNav && <Outlet />}
                </div>
            </div>
        </main>
    );
}
